import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { listAiChats } from "api-client";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import { cn, useSidebar } from "ui";

import type { AIChatListItem } from "src/components/aiChatTypes";

export const sidebarAiChatsQueryKey = ["sidebar-ai-chats"] as const;

export function SidebarChatTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sidebar = useSidebar();
  const params = useParams({ strict: false });
  const currentChatId = params.chatId ?? null;

  const chatsQuery = useQuery({
    queryKey: sidebarAiChatsQueryKey,
    queryFn: async (): Promise<AIChatListItem[]> => {
      const response = await listAiChats();
      return response.data.data.chats;
    },
    refetchOnWindowFocus: true,
  });

  const chats = chatsQuery.data ?? [];

  async function handleNewChat() {
    sidebar.setOpenMobile(false);
    await navigate({ to: "/ai" });
  }

  async function handleSelectChat(chatId: string) {
    sidebar.setOpenMobile(false);
    await navigate({
      to: "/ai/$chatId",
      params: { chatId: chatId },
    });
  }

  return (
    <div className="flex flex-col gap-1 p-2">
      <button
        type="button"
        onClick={() => {
          void handleNewChat();
        }}
        className="flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
      >
        <Icon name="tabler-edit" className="h-4 w-4" />
        <span>{t("aiChat.newChat")}</span>
      </button>
      {chatsQuery.isPending ? null : chats.length === 0 ? (
        <div className="px-3 py-2 text-sm text-muted-foreground">
          {t("aiChat.noChats")}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          <div className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("aiChat.recents")}
          </div>
          {chats.map((chat) => {
            const isSelected = chat.id === currentChatId;
            return (
              <button
                key={chat.id}
                type="button"
                onClick={() => {
                  void handleSelectChat(chat.id);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-muted/70 text-foreground"
                    : "text-foreground/85 hover:bg-muted/40",
                )}
              >
                <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                {chat.isStreaming ? (
                  <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-foreground" />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
